import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

/* ===== helpers fechas ===== */
function ymFirstDay(date){const d=new Date(date.getFullYear(),date.getMonth(),1);d.setHours(0,0,0,0);return d;}
function ymLastDay(date){const d=new Date(date.getFullYear(),date.getMonth()+1,0);d.setHours(23,59,59,999);return d;}
function addMonths(date,delta){const d=new Date(date);d.setMonth(d.getMonth()+delta);return d;}
function fmtYMD(d){const y=d.getFullYear();const m=String(d.getMonth()+1).padStart(2,"0");const day=String(d.getDate()).padStart(2,"0");return `${y}-${m}-${day}`;}
function getNXRangeExcludingCurrentPrev(n){const today=new Date();const startMonth=addMonths(today, -(n+1));const endMonth=addMonths(today, -2);return{from:ymFirstDay(startMonth),to:ymLastDay(endMonth)};}

/* ===== helpers numéricos % / formato ===== */
const NF = new Intl.NumberFormat("es-CL");
const fmtNum = (n) => NF.format(n ?? 0);
const calcPct = (pen, tot) => (tot > 0 ? (pen / tot) * 100 : 0);

// Chip de ofensor (color + etiqueta)
const ofensorInfo = (pct) => {
  if (pct <= 10) {
    return { label: "Bajo",  cls: "bg-yellow-500/20 border border-yellow-400/40 text-yellow-200" };
  } else if (pct <= 15) {
    return { label: "Medio", cls: "bg-orange-500/20 border border-orange-400/40 text-orange-200" };
  }
  return { label: "Alto",    cls: "bg-red-500/20 border border-red-400/40 text-red-200" };
};

export default function CanalPanel({ canal, tipo }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [periodos, setPeriodos] = useState({ ventas: [], n3: [], n6: [] });
  const [monthly, setMonthly] = useState([]);
  const [top10Ventas, setTop10] = useState([]);
  const [topPen3, setPen3] = useState([]);
  const [topPen6, setPen6] = useState([]);

  /* filtros Top10 */
  const [topLoading, setTopLoading] = useState(false);
  const [periodMode, setPeriodMode] = useState("n6");
  const [fromMonth, setFromMonth]   = useState("");
  const [toMonth, setToMonth]       = useState("");
  const [fSupervisor, setFSupervisor] = useState("");
  const [fEjCanal, setFEjCanal] = useState(""); // canal del ejecutivo (no confundir con canal de venta)

  // Carga por defecto (N6)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href="/login"; return; }
    (async () => {
      try {
        const { from, to } = getNXRangeExcludingCurrentPrev(6);
        const res = await axios.get("https://api.t-sales.cl/api/estadisticas/canal/top",{
          headers:{Authorization:`Bearer ${token}`},
          params:{ canal, tipo, ventas_from: fmtYMD(from), ventas_to: fmtYMD(to), limit: 10 }
        });
        setPeriodos(res.data?.periodos || {});
        setTop10(res.data?.top10Ventas || []);
        setPen3((res.data?.topPenalizadas3N || []).slice(0, 10));
        setPen6((res.data?.topPenalizadas6N || []).slice(0, 10));
      } catch {
        setErr("No se pudieron cargar las estadísticas.");
      } finally { setLoading(false); }
    })();
  }, [canal, tipo]);

  // Ventas mensuales (histórico)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get("https://api.t-sales.cl/api/estadisticas/canal/ventas-mensuales",{
      headers:{Authorization:`Bearer ${token}`},
      params:{ canal, tipo }
    }).then(res => setMonthly(res.data?.rows || [])).catch(()=>{});
  }, [canal, tipo]);

  const applyTopFilters = async () => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href="/login"; return; }
    setTopLoading(true); setErr("");

    let from,to;
    if (periodMode==="n3"){ const r=getNXRangeExcludingCurrentPrev(3); from=r.from; to=r.to; }
    else if (periodMode==="n6"){ const r=getNXRangeExcludingCurrentPrev(6); from=r.from; to=r.to; }
    else {
      if(!fromMonth || !toMonth){ setErr("Debes elegir ambos meses para el rango personalizado."); setTopLoading(false); return; }
      const fm=fromMonth.split("-").map(Number); const tm=toMonth.split("-").map(Number);
      from=ymFirstDay(new Date(fm[0], fm[1]-1, 1)); to=ymLastDay(new Date(tm[0], tm[1]-1, 1));
    }

    try{
      const params = {
        canal, tipo,
        ventas_from: fmtYMD(from),
        ventas_to: fmtYMD(to),
        limit: 50,
      };
      if (fSupervisor) params.supervisor = fSupervisor;
      if (fEjCanal)    params.e_canal    = fEjCanal;

      const res = await axios.get("https://api.t-sales.cl/api/estadisticas/canal/top",{
        headers:{Authorization:`Bearer ${token}`}, params
      });

      setPeriodos(res.data?.periodos || {});
      const list = res.data?.top10Ventas || [];
      setTop10(Array.isArray(list) && list.length>10 ? list.slice(0,10) : list);
      setPen3((res.data?.topPenalizadas3N || []).slice(0, 10));
      setPen6((res.data?.topPenalizadas6N || []).slice(0, 10));
    } catch {
      setErr("No se pudieron aplicar los filtros.");
    } finally { setTopLoading(false); }
  };

  const resetTopFilters = () => {
    setPeriodMode("n6"); setFromMonth(""); setToMonth("");
    setFSupervisor(""); setFEjCanal("");
    setTimeout(()=>applyTopFilters(),0);
  };

  if (loading) return <div className="py-10">Cargando…</div>;

  return (
    <div className="space-y-6">
      {/* Periodos contexto */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SmallCard title="Periodo Ventas (Top10)">
          <span className="text-sm">{periodos.ventas?.[0]} → {periodos.ventas?.[1]}</span>
        </SmallCard>
        <SmallCard title="Ventana N3 (Penalizadas)">
          <span className="text-sm">{periodos.n3?.[0]} → {periodos.n3?.[1]}</span>
        </SmallCard>
        <SmallCard title="Ventana N6 (Penalizadas)">
          <span className="text-sm">{periodos.n6?.[0]} → {periodos.n6?.[1]}</span>
        </SmallCard>
      </div>

      {/* Histórico mensual */}
      <Block title={`Ventas por mes (${tipo})`}>
        {monthly.length===0 ? (
          <div className="text-white/70 text-sm">Sin datos</div>
        ) : (
          <div style={{ width:"100%", height:320 }}>
            <ResponsiveContainer>
              <BarChart data={monthly} margin={{ top:10, right:20, left:0, bottom:0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.2)" strokeDasharray="3 3" />
                <XAxis dataKey="periodo"
                  tick={{ fill:"#FFFFFF", fontSize:12 }}
                  tickLine={{ stroke:"rgba(255,255,255,0.4)" }}
                  axisLine={{ stroke:"rgba(255,255,255,0.4)" }}
                  tickMargin={6}
                />
                <YAxis allowDecimals={false}
                  tick={{ fill:"#FFFFFF", fontSize:12 }}
                  tickLine={{ stroke:"rgba(255,255,255,0.4)" }}
                  axisLine={{ stroke:"rgba(255,255,255,0.4)" }}
                  tickMargin={6}
                />
                <Tooltip
                  wrapperStyle={{ outline:"none" }}
                  contentStyle={{ backgroundColor:"#FFFFFF", borderColor:"#64FFDA", color:"#0F1C17" }}
                  labelStyle={{ color:"#0F1C17", fontWeight:600 }}
                  itemStyle={{ color:"#0F1C17" }}
                  formatter={(v,n)=>[v, n==="ventas"?"Ventas":n]}
                />
                <Bar dataKey="ventas" name="Ventas" fill="#64FFDA" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Block>

      {/* Filtros Top10 */}
      <Block title={`Top 10 Ejecutivos por Ventas (${tipo})`}>
        <div className="mb-2 grid grid-cols-1 lg:grid-cols-12 items-center gap-2 lg:gap-3">
          {/* N3 / N6 / Rango */}
          <div className="lg:col-span-2">
            <div className="inline-flex w-fit bg-white/10 border border-white/20 rounded-lg overflow-hidden">
              <button onClick={()=>setPeriodMode("n3")}
                className={`h-9 px-3 text-sm ${periodMode==="n3"?"bg-[#64FFDA] text-[#0F1C17]":"hover:bg-white/5 text-white"}`}>
                N3
              </button>
              <button onClick={()=>setPeriodMode("n6")}
                className={`h-9 px-3 text-sm border-l border-white/20 ${periodMode==="n6"?"bg-[#64FFDA] text-[#0F1C17]":"hover:bg-white/5 text-white"}`}>
                N6
              </button>
              <button onClick={()=>setPeriodMode("custom")}
                className={`h-9 px-3 text-sm border-l border-white/20 ${periodMode==="custom"?"bg-[#64FFDA] text-[#0F1C17]":"hover:bg-white/5 text-white"}`}>
                Rango
              </button>
            </div>
          </div>

          {/* Desde / Hasta */}
          <div className="lg:col-span-2 min-w-0">
            <input type="month" className="h-9 bg-white/10 border border-white/20 rounded-lg px-3 text-sm w-full"
              value={fromMonth} onChange={(e)=>setFromMonth(e.target.value)} disabled={periodMode!=="custom"} />
          </div>
          <div className="lg:col-span-2 min-w-0">
            <input type="month" className="h-9 bg-white/10 border border-white/20 rounded-lg px-3 text-sm w-full"
              value={toMonth} onChange={(e)=>setToMonth(e.target.value)} disabled={periodMode!=="custom"} />
          </div>

          {/* Supervisor */}
          <div className="lg:col-span-3 min-w-0">
            <input type="text" placeholder="Supervisor…"
              className="h-9 bg-white/10 border border-white/20 rounded-lg px-3 text-sm w-full placeholder-white/60"
              value={fSupervisor} onChange={(e)=>setFSupervisor(e.target.value)} />
          </div>

          {/* Canal (del ejecutivo) */}
          <div className="lg:col-span-3 min-w-0">
            <input type="text" placeholder="Canal (ejecutivo)…"
              className="h-9 bg-white/10 border border-white/20 rounded-lg px-3 text-sm w-full placeholder-white/60"
              value={fEjCanal} onChange={(e)=>setFEjCanal(e.target.value)} />
          </div>
        </div>

        {/* Botones */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2 justify-end">
          <button onClick={resetTopFilters}
            className="h-9 px-4 rounded-xl text-sm border border-white/30 bg-white/10 hover:bg-white/20"
            disabled={topLoading}>
            Reiniciar filtros
          </button>
          <button onClick={applyTopFilters} disabled={topLoading}
            className="h-9 min-w-[140px] bg-[#64FFDA] text-[#0F1C17] px-4 rounded-xl font-semibold hover:opacity-90 disabled:opacity-60">
            {topLoading ? "Aplicando..." : "Aplicar"}
          </button>
        </div>

        {err && (
          <div className="mb-3 text-sm bg-red-500/20 border border-red-400/40 text-red-100 rounded-lg px-3 py-2">
            {err}
          </div>
        )}

        <Table
          headers={["#", "Ejecutivo", "RUT", "Canal", "Supervisor", "Ventas"]}
          rows={(top10Ventas||[]).map((r,idx)=>[
            idx+1, r.ejecutivo, r.rut||"—", r.canal||"—", r.supervisor||"—", r.ventas
          ])}
          empty={topLoading ? "Cargando..." : "Sin datos"}
        />
      </Block>

      {/* Penalizadas N3 */}
      <Block title={`TOP PENALIZADAS 3N (${tipo})`}>
        <Table
          headers={["#", "Ejecutivo", "RUT", "Canal", "Supervisor", "Penalizadas", "Tot Ventas", "%", "Ofensor"]}
          rows={(topPen3||[]).map((r,idx)=>{
            const tot = r.ventas_periodo ?? 0;
            const pen = r.penalizadas ?? 0;
            const pct = calcPct(pen, tot);
            const chip = ofensorInfo(pct);
            return [
              idx+1,
              r.ejecutivo,
              r.rut || "—",
              r.canal || "—",
              r.supervisor || "—",
              fmtNum(pen),
              fmtNum(tot),
              `${pct.toFixed(1)}%`,
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${chip.cls}`}>{chip.label}</span>,
            ];
          })}
          empty="Sin datos"
        />
      </Block>

      {/* Penalizadas N6 */}
      <Block title={`TOP PENALIZADAS 6N (${tipo})`}>
        <Table
          headers={["#", "Ejecutivo", "RUT", "Canal", "Supervisor", "Penalizadas", "Tot Ventas", "%", "Ofensor"]}
          rows={(topPen6||[]).map((r,idx)=>{
            const tot = r.ventas_periodo ?? 0;
            const pen = r.penalizadas ?? 0;
            const pct = calcPct(pen, tot);
            const chip = ofensorInfo(pct);
            return [
              idx+1,
              r.ejecutivo,
              r.rut || "—",
              r.canal || "—",
              r.supervisor || "—",
              fmtNum(pen),
              fmtNum(tot),
              `${pct.toFixed(1)}%`,
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${chip.cls}`}>{chip.label}</span>,
            ];
          })}
          empty="Sin datos"
        />
      </Block>
    </div>
  );
}

/* ===== UI helpers ===== */
function SmallCard({ title, children }) {
  return (
    <motion.div className="bg-white/10 border border-white/10 rounded-xl p-4"
      initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:0.25}}>
      <div className="text-xs text-white/80 mb-1">{title}</div>
      <div className="font-semibold">{children}</div>
    </motion.div>
  );
}
function Block({ title, children }) {
  return (
    <motion.div className="bg-white/10 border border-white/10 rounded-xl p-5"
      initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:0.25}}>
      <h3 className="text-lg font-bold mb-3">{title}</h3>
      {children}
    </motion.div>
  );
}
function Table({ headers, rows, empty }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left bg-white/10">
            {headers.map(h => <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {(!rows || rows.length===0) ? (
            <tr><td className="px-3 py-6 text-center text-white/70" colSpan={headers.length}>{empty}</td></tr>
          ) : rows.map((r,i)=>(
            <tr key={i} className="border-t border-white/10 hover:bg-white/5">
              {r.map((c,j)=> <td key={j} className="px-3 py-2 whitespace-nowrap">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
